import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { computeOverflow } from './computeOverflow';
import { createQuillControls } from './quillControls';

/** Fixed width of the ... trigger, mirrored in OverflowMenu.module.css. */
export const TRIGGER_WIDTH = 32;

const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Keeps the toolbar on one line.
 *
 * Slide commands and Quill's plain buttons compete on one priority scale;
 * Quill's pickers are pinned and never move. Widths are cached after a single
 * measure pass, so a window drag is arithmetic rather than repeated layout
 * reads, and the pass runs in a layout effect - which commits before paint - so
 * the deliberately overfull measuring frame is never seen.
 *
 * Every item is measured as the span from the previous mark's right edge to its
 * own. That folds gaps, group margins and Quill's group hairlines into whichever
 * item they sit in front of, so the arithmetic needs no model of the box tree.
 */
export function useToolbarOverflow({ toolbarRef, quillRef, menuMountRef, slideCommands }) {
  const adapterRef = useRef(null);
  const widths = useRef({});
  const paddingRef = useRef(0);

  const [quillItems, setQuillItems] = useState([]);
  const [measuring, setMeasuring] = useState(true);
  const [split, setSplit] = useState({ visibleIds: [], overflowIds: [], hasOverflow: false });

  const items = [
    ...slideCommands.map((command) => ({ id: command.id, priority: command.priority, movable: true })),
    ...quillItems,
  ];
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const key = items.map((item) => item.id).join('|');

  const settle = useCallback(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const available = toolbar.clientWidth - paddingRef.current;
    // The toolbar is hidden in some modes; measuring then would drop every
    // command, so keeping the last good split is the correct answer.
    if (available <= 0) return;

    const next = computeOverflow({
      items: itemsRef.current,
      widths: widths.current,
      available,
      triggerWidth: TRIGGER_WIDTH,
    });

    setSplit((prev) =>
      sameIds(prev.overflowIds, next.overflowIds) ? prev : next
    );
  }, [toolbarRef]);

  // Quill swaps its whole toolbar as the selection moves between a textbox, a
  // shape, a SmartArt node and the speaker notes.
  useEffect(() => {
    const adapter = createQuillControls({
      host: quillRef.current,
      onChange: () => {
        setQuillItems(adapterRef.current ? adapterRef.current.getItems() : []);
        setMeasuring(true);
      },
    });
    adapterRef.current = adapter;
    adapter.start();
    setQuillItems(adapter.getItems());

    return () => {
      adapter.stop();
      adapterRef.current = null;
    };
  }, [quillRef]);

  useLayoutEffect(() => {
    widths.current = {};
    setMeasuring(true);
  }, [key]);

  // Measure pass: everything is on the row right now, which is the one moment
  // all of it can be sized.
  useLayoutEffect(() => {
    if (!measuring) return;

    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    if (adapterRef.current) adapterRef.current.returnAll();

    const style = window.getComputedStyle(toolbar);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    paddingRef.current = padLeft + (parseFloat(style.paddingRight) || 0);

    const measured = {};
    let previousRight = toolbar.getBoundingClientRect().left + padLeft;

    Array.from(toolbar.querySelectorAll('[data-oid]')).forEach((mark) => {
      const rect = mark.getBoundingClientRect();
      const span = rect.right - previousRight;
      // A non-positive span means the row was not laid out on one line after
      // all - a wrapped mark sits to the *left* of the one before it. Falling
      // back to the mark's own width keeps the total sane instead of letting a
      // negative sneak in and convince the algorithm that everything fits.
      measured[mark.getAttribute('data-oid')] = span > 0 ? span : rect.width;
      previousRight = rect.right;
    });

    widths.current = measured;
    setMeasuring(false);
    settle();
  }, [measuring, toolbarRef, settle]);

  // Quill's controls are moved, not re-rendered, so this has to run after React
  // has committed the menu rows they are moving into.
  useLayoutEffect(() => {
    if (measuring || !adapterRef.current) return;
    adapterRef.current.apply(split.overflowIds, menuMountRef.current);
  });

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => settle());
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [toolbarRef, settle]);

  // Web fonts land after first paint and change every icon's metrics.
  useEffect(() => {
    const fonts = typeof document !== 'undefined' && document.fonts;
    if (!fonts || !fonts.ready || typeof fonts.ready.then !== 'function') return undefined;

    let cancelled = false;
    fonts.ready.then(() => {
      if (!cancelled) setMeasuring(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...split, quillItems, measuring };
}
